import { defineStore } from "pinia";
import { useAccountStore } from "@/_store/account.module";
import type { App } from "vue";
import { i18n } from "@/plugins/i18n";
import {
  WEBSOCKET_PING_INTERVAL_MS,
  WEBSOCKET_PONG_TIMEOUT_MS,
  WebsocketClientEvent,
  WebsocketMessageCode,
  WebsocketRawMessage,
} from "@/constants/websocket";
import { useTerminalStore } from "@/_store/terminal.module";
import { useSnackbarStore } from "@/_store/snackbar.module";
import { isJson } from "@coronet/cui";
import { useUsersSettingsTabStore } from "@/_store/users-settings/users-tab.module";
import { useUsersSettingsStore } from "@/_store/users-settings/users-settings.module";
import { useGroupsSettingsTabStore } from "@/_store/users-settings/user-groups-tab.module";
import router from "@/_helpers/router";
import { useExportsStore } from "@/_store/exports.module";
import { RouteName } from "@/constants/routes";
import { useTicketsStore } from "@/_store/tickets/tickets.module.ts";
import { useMspTerminalStore } from "@/_store/msp/msp-terminal.module.ts";
import { useWorkspaceStore } from "@/_store/workspace.module.ts";
import { StoreModule } from "@/constants/store.ts";
import { useGatewaySettingsStore } from "@/_store/gateway-settings.module.ts";
import { ProxyDirection } from "@/constants/email-proxy.ts";
import { useMspExportsStore } from "@/_store/msp/msp-exports.module.ts";
import { useVirtualOfficeStore } from "@/_store/network/virtual-office.module.ts";
import { useSetupHubStore } from "@/_store/setup-hub.module.ts";
import { SetupHubStatus, SetupHubStep } from "@/constants/setup-hub.ts";
import { isAccessRestricted } from "@/_helpers/permissions.ts";
import { RolePermissionsScope, WorkspaceManagementScopeSections } from "@/constants/roles.ts";

export type SocketStore = {
  isConnected: boolean;
  message: string;
  reconnectError: boolean;
  heartBeatTimer: number;
  reconnecting: boolean;
  lastMessageAt: number;
  pingSentAt: number;
  sessionRegistered: boolean;
  registeredWorkspaceId: string;
};

interface ShellSessionPayload {
  sessionId: number;
}

interface ShellCommandPayload extends ShellSessionPayload {
  commandResult: string;
  receivedAt: number;
}

interface OnboardingPayload {
  status: SetupHubStatus;
  step: SetupHubStep;
}

type GenericPayload = Record<string, unknown>;

type Message<C extends WebsocketMessageCode, P> = {
  code: C;
  status: "ok" | "failed";
  payload: P;
};

export type WebsocketMessageWithPayload =
  | Message<WebsocketMessageCode.SHELL_SESSION_OPENED, ShellSessionPayload>
  | Message<WebsocketMessageCode.SHELL_SESSION_OPENING_FAILED, ShellSessionPayload>
  | Message<WebsocketMessageCode.SHELL_SESSION_CLOSED, ShellSessionPayload>
  | Message<WebsocketMessageCode.TOO_MANY_SHELL_SESSIONS_OPENED, ShellSessionPayload>
  | Message<WebsocketMessageCode.SHELL_COMMAND_RESPONSE, ShellCommandPayload>
  | Message<WebsocketMessageCode.SHELL_COMMAND_CONFIRMATION_REQUEST, ShellCommandPayload>
  | Message<WebsocketMessageCode.ONBOARDING_UPDATED, OnboardingPayload>
  | Message<WebsocketMessageCode.IMPORT_USERS_FROM_CSV, GenericPayload>
  | Message<WebsocketMessageCode.IMPORT_ALLOW_BLOCK_LIST, GenericPayload>
  | Message<WebsocketMessageCode.OPERATOR_EXPORT_ADDED, GenericPayload>
  | Message<WebsocketMessageCode.CSV_EXPORT, GenericPayload>;

export const useSocketStore = (app: App<Element>) => {
  return defineStore(StoreModule.WEBSOCKET, {
    state: (): SocketStore => ({
      isConnected: false,
      message: "",
      reconnectError: false,
      heartBeatTimer: 0,
      reconnecting: false,
      lastMessageAt: 0,
      pingSentAt: 0,
      sessionRegistered: false,
      registeredWorkspaceId: "",
    }),
    actions: {
      SOCKET_ONOPEN(event: Event) {
        app.config.globalProperties.$socket = event.currentTarget;
        this.isConnected = true;
        useTerminalStore().setWebsocketConnected(true);
        const wasReconnecting = this.reconnecting;
        this.reconnecting = false;
        console.log("WebSocket connected");
        const { token } = useAccountStore();
        const workspaceStore = useWorkspaceStore();
        const workspaceId = workspaceStore.workspace.workspaceId;
        this.sessionRegistered = registerSession(app, token, workspaceId);
        this.registeredWorkspaceId = this.sessionRegistered && workspaceId ? workspaceId : "";
        this.lastMessageAt = Date.now();
        this.pingSentAt = 0;
        window.clearInterval(this.heartBeatTimer);
        this.heartBeatTimer = window.setInterval(() => {
          this.probeConnectionLiveness();
        }, WEBSOCKET_PING_INTERVAL_MS);
        if (wasReconnecting) {
          reconcileVpnConnectionStatusIfOnNetworkView();
        }
      },
      SOCKET_ONCLOSE() {
        setTimeout(() => {
          if (app.config.globalProperties.$socket.readyState !== 1) {
            this.isConnected = false;
            useTerminalStore().setWebsocketConnected(false);
            window.clearInterval(this.heartBeatTimer);
            this.heartBeatTimer = 0;
            this.pingSentAt = 0;
            this.sessionRegistered = false;
            this.registeredWorkspaceId = "";
          }
        }, 3000);
      },
      SOCKET_ONERROR(event: Event) {
        console.error("WebSocket error", event);
      },
      SOCKET_ONMESSAGE(msg: MessageEvent) {
        this.message = msg.data;
        this.lastMessageAt = Date.now();
        handleWebsocketMessage(msg);
      },
      probeConnectionLiveness() {
        if (!this.isConnected || !this.sessionRegistered) {
          return;
        }
        const socket = app.config.globalProperties.$socket;
        if (!socket || socket.readyState !== 1) {
          return;
        }
        const isAwaitingPong = this.pingSentAt > 0 && this.lastMessageAt < this.pingSentAt;
        if (isAwaitingPong) {
          if (Date.now() - this.pingSentAt >= WEBSOCKET_PONG_TIMEOUT_MS) {
            socket.close();
          }
          return;
        }
        socket.send(JSON.stringify({ event: WebsocketClientEvent.PING }));
        this.pingSentAt = Date.now();
      },
      syncSessionRegistration() {
        const socket = app.config.globalProperties.$socket;
        if (!this.isConnected || !socket || socket.readyState !== 1) {
          return;
        }
        const { token } = useAccountStore();
        const workspaceId = useWorkspaceStore().workspace.workspaceId;
        if (!token || !workspaceId) {
          return;
        }
        if (this.sessionRegistered) {
          if (this.registeredWorkspaceId !== workspaceId) {
            socket.close();
          }
          return;
        }
        this.sessionRegistered = registerSession(app, token, workspaceId);
        if (this.sessionRegistered) {
          this.registeredWorkspaceId = workspaceId;
        }
      },
      SOCKET_RECONNECT(count: number) {
        this.reconnecting = true;
        console.info("Reconnecting WebSocket...", count);
      },
      SOCKET_RECONNECT_ERROR() {
        this.reconnectError = true;
      },
    },
  })();
};

function reconcileVpnConnectionStatusIfOnNetworkView() {
  const currentRoute = router.currentRoute.value;
  const isNetworkConfigurationView =
    currentRoute.name === RouteName.NETWORK_SETTINGS_GENERAL ||
    currentRoute.name === RouteName.SECURE_WEB_GATEWAY_SETTINGS ||
    currentRoute.matched.some((route) => route.name === RouteName.NETWORK_PAGE);
  if (isNetworkConfigurationView) {
    useVirtualOfficeStore().getVpnConnection(true);
  }
}

function registerSession(app: App<Element>, token?: string, workplaceId?: string): boolean {
  // if there is no token or workplace, not registering session
  if (!token || !workplaceId) {
    return false;
  }
  const message = {
    event: WebsocketClientEvent.SESSION_REGISTRATION,
    data: {
      token,
      workplaceId,
    },
  };
  if (app.config.globalProperties.$socket && app.config.globalProperties.$socket.readyState === 1) {
    app.config.globalProperties.$socket.send(JSON.stringify(message));
    console.log("session registered");
    return true;
  }
  return false;
}

function handleWebsocketMessage(msg: MessageEvent) {
  if (isJson(msg.data)) {
    return handleMessageWithPayload(JSON.parse(msg.data) as WebsocketMessageWithPayload);
  }
  return handleStringMessage(msg.data);
}

function handleStringMessage(message: string) {
  switch (message as WebsocketRawMessage) {
    case WebsocketRawMessage.PONG:
      break;
    case WebsocketRawMessage.USERS_UPDATED:
      if (router.currentRoute.value.name === RouteName.USERS_SETTINGS_USERS_TAB) {
        useUsersSettingsTabStore().getUsers();
        return;
      }
      if (router.currentRoute.value.name === RouteName.USERS_SETTINGS_GROUPS_TAB) {
        useGroupsSettingsTabStore().init();
        return;
      }
      if (
        !isAccessRestricted(
          RolePermissionsScope.WORKSPACE_MANAGEMENT,
          WorkspaceManagementScopeSections.USERS
        )
      ) {
        useSnackbarStore().addGenericSuccess(i18n.global.t("snackbar.messages.user.usersUpdated"));
        useUsersSettingsTabStore().getUsers();
      }
      break;
    case WebsocketRawMessage.TICKETS_UPDATED:
      // TODO: uncomment after tickets migration
      // if (router.currentRoute.value.name === "tickets") {
      //   useSnackbarStore().addGenericSuccess(i18n.global.t("snackbar.messages.event.eventsUpdated"))
      //   useTicketsStore().init();
      // }
      break;
    case WebsocketRawMessage.SUBSCRIPTION_UPDATED:
      useWorkspaceStore().getCurrentWorkspace();
      break;
    case WebsocketRawMessage.VPN_CONNECTION_UPDATED:
      reconcileVpnConnectionStatusIfOnNetworkView();
      break;
    case WebsocketRawMessage.OUTBOUND_PROXY_TEST_FAILED:
      if (router.currentRoute.value.name === RouteName.GATEWAY_SETTINGS_OUTBOUND) {
        useGatewaySettingsStore().init(ProxyDirection.OUTBOUND);
      }
      useSnackbarStore().add({
        html: `
        ${i18n.global.t("snackbar.messages.outboundGateway." + WebsocketRawMessage.OUTBOUND_PROXY_TEST_FAILED)}
      `,
        redirectTo: RouteName.GATEWAY_SETTINGS_OUTBOUND,
        redirectToText: i18n.global.t("snackbar.messages.outboundGateway.goToGatewaySettings"),
      });
      break;
    case WebsocketRawMessage.OUTBOUND_PROXY_TEST_SUCCESSFUL:
      if (router.currentRoute.value.name === RouteName.GATEWAY_SETTINGS_OUTBOUND) {
        useGatewaySettingsStore().init(ProxyDirection.OUTBOUND);
      }
      useSnackbarStore().add({
        html: `
        ${i18n.global.t("snackbar.messages.outboundGateway." + WebsocketRawMessage.OUTBOUND_PROXY_TEST_SUCCESSFUL)}
      `,
        redirectTo: RouteName.GATEWAY_SETTINGS_OUTBOUND,
        redirectToText: i18n.global.t("snackbar.messages.outboundGateway.goToGatewaySettings"),
      });
      break;
    case WebsocketRawMessage.OUTBOUND_PROXY_BLOCK_MODE_ACTIVE:
      useSnackbarStore().add({
        html: `
        ${i18n.global.t("snackbar.messages.outboundGateway." + WebsocketRawMessage.OUTBOUND_PROXY_BLOCK_MODE_ACTIVE)}
      `,
        redirectTo: RouteName.GATEWAY_SETTINGS_OUTBOUND,
        redirectToText: i18n.global.t("general.view"),
      });
      break;
    case WebsocketRawMessage.OUTBOUND_PROXY_BLOCK_MODE_AVAILABLE:
      useSnackbarStore().add({
        html: `
        ${i18n.global.t("snackbar.messages.outboundGateway." + WebsocketRawMessage.OUTBOUND_PROXY_BLOCK_MODE_AVAILABLE)}
      `,
        redirectTo: RouteName.GATEWAY_SETTINGS_OUTBOUND,
        redirectToText: i18n.global.t("general.view"),
      });
      break;
  }
}

function handleMessageWithPayload(msg: WebsocketMessageWithPayload) {
  const isMspDevicesPage = router.currentRoute.value.name === RouteName.MSP_DEVICES_TAB;
  const terminalStore = isMspDevicesPage ? useMspTerminalStore() : useTerminalStore();
  const sessionId = terminalStore.sessionId;
  switch (msg.code) {
    case WebsocketMessageCode.SHELL_SESSION_OPENING_FAILED:
    case WebsocketMessageCode.SHELL_SESSION_CLOSED:
    case WebsocketMessageCode.TOO_MANY_SHELL_SESSIONS_OPENED:
      if (sessionId === msg.payload.sessionId) {
        terminalStore.setFailReason(msg.code);
      }
      break;
    case WebsocketMessageCode.SHELL_SESSION_OPENED:
      if (sessionId === msg.payload.sessionId) {
        terminalStore.setSessionOpened(true);
      }
      break;
    case WebsocketMessageCode.SHELL_COMMAND_RESPONSE:
    case WebsocketMessageCode.SHELL_COMMAND_CONFIRMATION_REQUEST:
      if (sessionId === msg.payload.sessionId) {
        const getLastCommandReceivedTime = () => {
          if (msg.code === WebsocketMessageCode.SHELL_COMMAND_RESPONSE) return null;
          // Check if the last command (by checking the last received time of command) and command that we get
          // are the same, then we skip showing this command
          // if not - then there is a possibility that something is wrong with WS, and we already got the output for that command via API call
          return terminalStore.lastCommandReceivedTime === msg.payload.receivedAt
            ? terminalStore.lastCommandReceivedTime
            : msg.payload.receivedAt;
        };
        terminalStore.setCommandInProgress(false);
        terminalStore.setLastCommandResponseType(msg.code);
        terminalStore.setLastCommandReceivedTime(getLastCommandReceivedTime());
        terminalStore.emitEvent({
          type: "response",
          payload: msg.payload.commandResult,
        });
      }
      break;
    case WebsocketMessageCode.IMPORT_ALLOW_BLOCK_LIST:
      // if (router.currentRoute.value.name === "allow_block_list_tab") {
      //   dispatch("emailsSettings/getAllowBlockList", null, { root: true });
      //   dispatch("snackbar/add", snackbarMessage, { root: true });
      // }
      break;
    case WebsocketMessageCode.OPERATOR_EXPORT_ADDED:
    case WebsocketMessageCode.CSV_EXPORT:
      if (router.currentRoute.value.name === RouteName.REPORTS_EXPORTS_TAB) {
        useExportsStore().getExportsList();
      }
      if (router.currentRoute.value.name === RouteName.MSP_REPORTS_EXPORTS_TAB) {
        useMspExportsStore().getExportsList();
      }
      break;

    case WebsocketMessageCode.IMPORT_USERS_FROM_CSV:
      if (router.currentRoute.value.name === RouteName.USERS_SETTINGS_USERS_TAB) {
        useUsersSettingsStore().init();
        useUsersSettingsTabStore().getUsers();
      }
      break;
    case WebsocketMessageCode.ONBOARDING_UPDATED:
      useSetupHubStore().getOnboardingStatus();
      if (msg.payload.status === SetupHubStatus.COMPLETED) {
        useSetupHubStore().completeStepFromWebsocket(msg.payload.step);
      }
      break;
    default: {
      const fallback = msg as unknown as {
        code: string;
        status: "ok" | "failed";
        payload?: { service?: string } & Record<string, unknown>;
      };
      const snackbarMessage = i18n.global.t(
        `snackbar.messages.socket.${fallback.code}.${fallback.status}`,
        {
          ...fallback.payload,
          service: i18n.global.t(`services.${fallback.payload?.service}`),
        }
      );
      useSnackbarStore().addGenericSuccess(snackbarMessage);
      if (router.currentRoute.value.name === RouteName.TICKETS) {
        useTicketsStore().getTickets(false);
      }
      break;
    }
  }
}
